/* eslint-disable arrow-parens */
import { useCallback, useEffect, useContext, useState, useMemo, Suspense } from "react";
import { Paper, Grid, Checkbox } from "@mui/material";
import PlanHeader from "../../../../../../shared/enrollment/planCode/PlanHeader";
import LinkIcon from "@mui/icons-material/Link";
import CircleIcon from "@mui/icons-material/Circle";
import { AuthContext, GroupEnrollmentContext, PaycheckContext, ThemeContext } from "../../../../../../../contexts";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { CustomInput } from "../../../../../../shared/CustomInput";
import { useHistory, useLocation } from "react-router-dom";
import PlanActions from "../../../../../../shared/enrollment/planActions/PlanActions";
import { CoverageDetails } from "../../../../../../shared/enrollment";
import { numberToDollarStr } from "../../../../../../../utils/commonFunctions/dollar";
import { PremiumRateCoverage } from "../../../../../../../types/plan.types";
import {
	dateTimeZoneConverter,
	getCurrentDate,
	manualDateComparator
} from "../../../../../../../utils/commonFunctions/date";
import { CommonDetails } from "../../../../../../../types/enrollment.types";
import { api } from "../../../../../../../utils/api";
import { AddEnrollmentPayload } from "../../../../../../../utils/api/enrollment/enrollment";
import { CustomFormDialogType, CustomWaiveForm } from "../../../../../../../types/components/dialogProps.types";
import { generateGSTDWaiveEnrollmentPayload } from "../../../../../../../utils/commonFunctions/groupEnrollment/enrollment";
import { LazyCustomFormDialog, LazyCustomWaiveFormDialog } from "../../../../../../shared";
import { trackPromise } from "react-promise-tracker";
import { GLTDClass1PlanBrochure, GLTDClass2PlanBrochure } from "../../../../../../../assets";

import "./IntercityGLTDForm.css";
import { WaiveCoverageFormType } from "../../../../../../../types/waiveDialogForm.types";
import { generateICIGLTDPlanWaiveEnrollmentPayload } from "../../../../../../../utils/commonFunctions/ICIEnrollment/ICICoreEnrollment/ICICoreEnrollment";

const GLTDForm = ({ dependents, getEnrollmentInitData, openEnrollment }: any): JSX.Element => {
	const { member } = useContext(AuthContext);
	const [premiumAmount, setPremiumAmount] = useState<number | null>(null);
	const [checkboxOption, setCheckboxOption] = useState("");
	const { paycheck } = useContext(PaycheckContext);
	const [coverageDetails, setCoverageDetails] = useState<any[]>([]);
	const { theme } = useContext(ThemeContext);
	const history = useHistory();
	const location = useLocation();
	const [customFormDialogProps, setCustomFormDialogProps] = useState<CustomWaiveForm>();
	const [waiveCoverageForm, setWaiveCoverageForm] = useState<WaiveCoverageFormType>({
		waive_reason: "",
		other_waive_coverage: "",
		waive_employee_cost: false
	});
	const [submitWaiveFormStatus, setSubmitWaiveFormStatus] = useState<boolean>(false);
	const urlSearchParams: URLSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

	const handleWaiveReasonChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { value, name, checked, type } = event.target;

		setWaiveCoverageForm((prevValue) => {
			return {
				...prevValue, // Using spread operator for a shallow copy
				[name]: type === "checkbox" ? checked : value
			};
		});
	}, []);

	const handleWaiveEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null) {
			return;
		}

		const { standardDetails, commonDetails } = generateICIGLTDPlanWaiveEnrollmentPayload(
			member,
			openEnrollment,
			waiveCoverageForm
		);

		const enrollmentPayload: AddEnrollmentPayload = {
			standard_details: standardDetails,
			common_details: commonDetails
		};

		const response = await api.enrollment.addEnrollment(enrollmentPayload);

		if (response) {
			getEnrollmentInitData(false);
			const step = urlSearchParams.get("step");
			history.push(`?step=${step}&stage=2`);
		}
	}, [getEnrollmentInitData, history, member, openEnrollment, urlSearchParams, waiveCoverageForm]);

	const handleWaiveSubmition = useCallback(
		(waiveCoverageForm: WaiveCoverageFormType) => {
			setCustomFormDialogProps(
				Object.assign({}, customFormDialogProps, {
					openDialog: false
				})
			);
			handleWaiveEnrollment();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[customFormDialogProps, waiveCoverageForm]
	);

	const handleWaiveSubmitClick = useCallback(() => {
		setSubmitWaiveFormStatus((prevValue) => !prevValue);
	}, []);

	const handleWaiveButtonClick = useCallback(() => {
		setCustomFormDialogProps(
			Object.assign({}, customFormDialogProps, {
				openDialog: true,
				title: "Waivers Coverage Form",
				description:
					"The Group Health Plan has been offered to me and after seriously considering benefits I have decided not to enroll myself or my dependents in the Plan. I understand that i and my dependents must wait until the next open enrollment period unless a change of family and/or job status occur",

				inputFields: [
					{
						label: "Coverage Elsewhere",
						name: "waive_reason",
						type: "textfield",
						onChange: handleWaiveReasonChange,
						value: waiveCoverageForm.waive_reason,
						placeholder: "Name of carrier"
					},
					{
						label: "Employee Cost",
						name: "waive_employee_cost",
						type: "checkbox",
						onChange: handleWaiveReasonChange,
						value: waiveCoverageForm.waive_employee_cost,
						placeholder: ""
					},
					{
						label: "Other",
						name: "other_waive_coverage",
						type: "textfield",
						onChange: handleWaiveReasonChange,
						value: waiveCoverageForm.other_waive_coverage,
						placeholder: "State the other reason"
					}
				],
				actions: [
					{
						label: "Cancel",
						callback: () =>
							setCustomFormDialogProps(
								Object.assign({}, customFormDialogProps, {
									openDialog: false
								})
							)
					},
					{
						label: "Yes",
						callback: () => handleWaiveSubmitClick()
					}
				]
			})
		);
	}, [customFormDialogProps, handleWaiveReasonChange, handleWaiveSubmitClick, waiveCoverageForm]);

	const getPremiumAmount = useCallback(
		(premiumRateCoverage: PremiumRateCoverage): string => {
			let premiumAmount = "";
			if (paycheck) {
				if (premiumRateCoverage.premium_type === "FIXED") {
					premiumAmount =
						(paycheck.pay_frequency === "WEEKLY"
							? premiumRateCoverage.weekly_premium
							: premiumRateCoverage.monthly_premium) + "";
					premiumAmount = `${numberToDollarStr(Number(premiumAmount))}`;
				} else if (premiumRateCoverage.premium_type === "PERCENTAGE") {
					premiumAmount =
						(paycheck.pay_frequency === "WEEKLY"
							? premiumRateCoverage.weekly_premium
							: premiumRateCoverage.monthly_premium) + "";
					premiumAmount = premiumAmount + "%";
				}
			}
			return premiumAmount;
		},
		[paycheck]
	);

	const getPremiumRateByFixed = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (paycheck === null) {
				return null;
			}
			let premiumAmount: number | null = null;
			if (paycheck.pay_frequency === "MONTHLY") {
				premiumAmount = coverageCode.monthly_premium as number;
			} else if (paycheck.pay_frequency === "WEEKLY") {
				premiumAmount = coverageCode.weekly_premium as number;
			}
			return premiumAmount;
		},
		[paycheck]
	);

	const getPremiumRateByPercentage = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (paycheck === null) {
				return null;
			}
			let premiumAmount: number | null = null;
			if (paycheck.pay_frequency === "MONTHLY") {
				premiumAmount = (basePayRate * ((coverageCode.monthly_premium as number) / 100)) as number;
				if (premiumAmount > (coverageCode.monthly_maximum_premium as number)) {
					premiumAmount = coverageCode.monthly_maximum_premium as number;
				} else if (premiumAmount < (coverageCode.monthly_minimum_premium as number)) {
					premiumAmount = coverageCode.monthly_minimum_premium as number;
				}
			} else if (paycheck.pay_frequency === "WEEKLY") {
				premiumAmount = (basePayRate * ((coverageCode.weekly_premium as number) / 100)) as number;
				if (premiumAmount > (coverageCode.weekly_maximum_premium as number)) {
					premiumAmount = coverageCode.weekly_maximum_premium as number;
				} else if (premiumAmount < (coverageCode.weekly_minimum_premium as number)) {
					premiumAmount = coverageCode.weekly_minimum_premium as number;
				}
			}
			return premiumAmount;
		},
		[paycheck]
	);

	const calculatePremiumByCoverage = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (openEnrollment === null || paycheck === null) {
				return null;
			}

			let premiumAmount: number | null;
			if (coverageCode.active === false) {
				alert("Member is not covered in this plan");
				return null;
			}
			if (coverageCode.premium_type === "PERCENTAGE") {
				premiumAmount = getPremiumRateByPercentage(basePayRate, coverageCode);
			} else if (coverageCode.premium_type === "FIXED") {
				premiumAmount = getPremiumRateByFixed(basePayRate, coverageCode);
			} else {
				alert("Invalid premium type");
				return null;
			}
			return premiumAmount;
		},
		[getPremiumRateByFixed, getPremiumRateByPercentage, openEnrollment, paycheck]
	);

	const getCoverageDetails = useCallback(() => {
		const _coverageDetails: any[] = [];
		if (openEnrollment === null || paycheck === null) {
			return;
		}
		const {
			plan: { coverage }
		} = openEnrollment;
		if (coverage.EE.active === true) {
			const premiumAmount = getPremiumAmount(coverage.EE);

			_coverageDetails.push({
				coverage: "Employee Only",
				premiumAmount
			});
		}
		setCoverageDetails(Object.assign([], _coverageDetails));
	}, [getPremiumAmount, openEnrollment, paycheck]);

	const calculatePremium = useCallback(() => {
		if (openEnrollment === null || paycheck === null) {
			return null;
		}

		let basePayRate: number;
		if (paycheck?.pay_frequency === "WEEKLY") {
			basePayRate = paycheck.weekly_base_rate;
		} else if (paycheck?.pay_frequency === "MONTHLY") {
			basePayRate = paycheck.monthly_base_rate;
		} else {
			alert("Invalid Pay Frequency");
			return;
		}
		let employeePremium: number | null = 0;
		employeePremium = calculatePremiumByCoverage(basePayRate, openEnrollment.plan.coverage.EE);
		setPremiumAmount(Number(employeePremium?.toFixed(2)));
		if (employeePremium === null) {
			return;
		}
		if (openEnrollment) {
			// setCurrentEnrollment(
			// 	Object.assign(
			// 		{},
			// 		{
			// 			plan_name: openEnrollment.plan.plan_name,
			// 			status: "Current",
			// 			premium_amount: employeePremium
			// 		}
			// 	)
			// );
		}
	}, [calculatePremiumByCoverage, openEnrollment, paycheck]);

	const handleDialogClose = () => {
		setCustomFormDialogProps(
			Object.assign({}, customFormDialogProps, {
				openDialog: false
			})
		);
	};

	const handleActivateEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null || paycheck === null || premiumAmount === null) {
			return;
		}

		const currentDate = new Date() as unknown as string;
		const planStartDate = openEnrollment.plan.start_date; // -1 or 0
		const planDateComparison: number = manualDateComparator(planStartDate, currentDate);

		const effectiveDate = openEnrollment.plan.auto_approve
			? planDateComparison === -1 || planDateComparison === 0
				? getCurrentDate()
				: null
			: null;
		const enrollmentCommonDetails: CommonDetails = {
			agent_id: null,
			location_number: member.location.location_number,
			location_name: member.location.location_name,
			group_number: member.group_number,
			group_name: member.group.name,
			plan_object_id: openEnrollment.plan._id,
			plan_code: openEnrollment.plan.plan_code,
			plan_name: openEnrollment.plan.plan_name,
			base_plan_name: openEnrollment.plan.plan_name,
			insured_object_id: member._id,
			insured_SSN: member.SSN,
			writing_number: 0,
			unenrolled_reason: null,
			waive_reason: null,
			termination_reason: null,
			enrollment_date: getCurrentDate(),
			effective_date: effectiveDate,
			termination_date: null,
			open_enrollment_id: openEnrollment._id,
			total_premium: premiumAmount
		};
		const intercityGLTDEnrollmentPayload: AddEnrollmentPayload = {
			common_details: enrollmentCommonDetails,
			standard_details: [
				{
					member_object_id: member._id,
					member_SSN: member.SSN,
					coverage_code: "EE",
					premium_amount: premiumAmount,
					is_tobacco: false
				}
			]
		};

		const response = await trackPromise(api.enrollment.addEnrollment(intercityGLTDEnrollmentPayload));
		if (response) {
			const step = urlSearchParams.get("step");
			getEnrollmentInitData(false);
			history.push(`?step=${step}&stage=0`);
		}
	}, [getEnrollmentInitData, history, member, openEnrollment, paycheck, premiumAmount, urlSearchParams]);

	useEffect(() => {
		getCoverageDetails();
		calculatePremium();
	}, [calculatePremium, getCoverageDetails]);

	useEffect(() => {
		if (submitWaiveFormStatus && waiveCoverageForm) handleWaiveSubmition(waiveCoverageForm);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submitWaiveFormStatus, waiveCoverageForm]);

	if (!member) return <></>;

	return (
		<div className="GSTD theme-container">
			<Suspense fallback={<div />}>
				{customFormDialogProps ? (
					<LazyCustomWaiveFormDialog
						customFormDialogProps={customFormDialogProps}
						handleDialogClose={handleDialogClose}
						date={dateTimeZoneConverter(getCurrentDate()) as unknown as string}
					/>
				) : null}
			</Suspense>
			<Paper className="paper-container">
				<PlanHeader planCode={openEnrollment?.plan.plan_code} planName="Group Long Term Disability" />
				<div
					className="theme-divider-grad-mask"
					style={{
						background: `linear-gradient(90deg, ${theme.primary_color} 0 55%, transparent 0 100%) 0 0 / 2rem 1rem`
					}}
				/>
				<div className="theme-plan-section-margin" />

				<div className="theme-plan-inner-section-margin" />

				<div className="theme-plan-section-margin" />
				<CoverageDetails coverageDetails={coverageDetails} />
				<div className="theme-plan-section-margin" />
				<div className="theme-plan-icon-item-container">
					<div>
						<EventNoteIcon className="event-icon" style={{ color: theme.primary_color }} />
					</div>
					<div className="theme-plan-text">
						<strong>Note:</strong> All salaried employees enrollned in medical plan must enroll in Group
						Long Term Disability.
					</div>
				</div>
				<div style={{ marginTop: "2rem" }} />

				{/* ============================================== PREMIUM AMOUNT ============================================ */}
				<Grid className="grid-container" container columnSpacing={2}>
					<Grid container columnSpacing={2}>
						<Grid item xl={4} lg={4} md={4} sm={5} xs={6}>
							<div className="details-form-label  required">Group Plan</div>
						</Grid>
						<Grid item xl={3} lg={3} md={3} sm={5} xs={6}>
							<div className="details-form-row">
								<div className="details-form-label"></div>
							</div>
						</Grid>
						<Grid item xl={3} lg={3} md={3} sm={5} xs={6}>
							<div className="details-form-row">
								<div className="details-form-label"></div>
							</div>
						</Grid>
						<Grid item xl={2} lg={2} md={2} sm={2} xs={6}>
							<div className="details-form-row">
								<div className="details-form-label align-center">Premium</div>
								<div className="show-premium"></div>
							</div>
						</Grid>
					</Grid>

					<Grid container columnSpacing={2}>
						<Grid item xl={5} lg={5} md={3} sm={5} xs={6}>
							<div style={{ display: "flex" }}>
								<div className="checkbox-label details-form-row">
									<div className="details-form-label">Group Long Term Disability</div>
								</div>
							</div>
						</Grid>
						<Grid item xl={5} lg={5} md={3} sm={5} xs={6}></Grid>
						<Grid item xl={2} lg={2} md={3} sm={2} xs={6}>
							<div className="details-form-row margin-top-minus-8">
								<div className="show-premium">$ {premiumAmount}</div>
							</div>
						</Grid>
					</Grid>
				</Grid>

				{/* <div>
					<div className="details-form-row">
						<div className="details-form-label  required">Premium Amount</div>
						<CustomInput value={`$ ${premiumAmount}`} disabled />
					</div>
				</div> */}
				<div>
					<h3>Please sign below </h3>
					<div className="herby-terms-conditions">
						I hereby,
						<div className="terms-container">
							<div>
								<LinkIcon className="link-icon" style={{ color: theme.primary_color }} />
							</div>
							<div>
								Request to enroll in that{" "}
								<a
									href=""
									// onClick={redirectBrochureDownloiad}
								>
									BENEFIT PLAN
								</a>{" "}
								for which I am or may become eligible under the Plan created by the above{" "}
								<a
									href=""
									// onClick={redirectBrochureDownloiad}
								>
									PLAN SPONSOR
								</a>
							</div>
						</div>
						<div className="terms-container">
							<div>
								<LinkIcon className="link-icon" style={{ color: theme.primary_color }} />
							</div>
							<div>
								Reafirm that I will abide by the provisions set forth in the{" "}
								<a
									href=""
									// onClick={redirectBrochureDownloiad}
								>
									SUMMARY PLAN DESCRIPTION
								</a>{" "}
								and{" "}
								<a
									href=""
									// onClick={redirectBrochureDownloiad}
								>
									PLAN DOCUMENT
								</a>
							</div>
						</div>
						<div className="terms-container">
							<div>
								<LinkIcon className="link-icon" style={{ color: theme.primary_color }} />
							</div>
							<div>Authorize the required deductions, if any, from my earnings</div>
						</div>
						<div className="terms-container">
							<div>
								<LinkIcon className="link-icon" style={{ color: theme.primary_color }} />
							</div>
							<div>Certify that the above dates of birth and other information are correct</div>
						</div>
						<div className="terms-container">
							<div>
								<LinkIcon className="link-icon" style={{ color: theme.primary_color }} />
							</div>
							<div>
								I understand that cannot change or drop coverage prior to next open enrollment period
								unless a change of family and/or job status occur.
							</div>
						</div>
					</div>
				</div>

				<div className="theme-plan-section-margin" />
				<div className="option-content">
					<Checkbox defaultChecked style={{ paddingLeft: 0 }} />
					<p className="checkbox-label">
						<strong>
							I UNDERSTAND AND AGREE TO THE TERMS AND CONDITIONS STATED ABOVE AND AUTHORIZE
							THE&nbsp;PAYROLL DEDUCTIONS TO BE MADE AS INDICATED
						</strong>
					</p>
				</div>
				<div className="theme-plan-section-margin" />
				<Grid container>
					<Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
						<div className="details-form-row">
							<div className="details-form-label  required">Signature</div>
							<CustomInput value={member.first_name + " " + member.last_name} disabled />
						</div>
					</Grid>
					<Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
						<div className="details-form-row">
							<div className="details-form-label  required">Date</div>
							<CustomInput value={dateTimeZoneConverter(getCurrentDate())} disabled />
						</div>
					</Grid>
				</Grid>
				<div className="theme-plan-section-margin" />
				<PlanActions
					waiveButtonCallback={handleWaiveButtonClick}
					activateButtonCallback={handleActivateEnrollment}
				/>
			</Paper>
		</div>
	);
};

export default GLTDForm;
